if (!customElements.get('video-gallery')) {
  class PopupVideoGallery extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.grid = this.querySelector('[data-gallery-grid]');
      this.cards = Array.from(this.querySelectorAll('[data-gallery-card]'));
      this.dialog = this.querySelector('[data-gallery-dialog]');
      this.stage = this.querySelector('[data-gallery-stage]');
      this.loading = this.querySelector('[data-gallery-loading]');
      this.dialogTitle = this.querySelector('[data-gallery-dialog-title]');
      this.dialogCaption = this.querySelector('[data-gallery-dialog-caption]');
      this.preloadBin = this.querySelector('[data-gallery-preload-bin]');
      this.lastTrigger = null;
      this.preloadQueue = [];
      this.activePreload = false;

      this.handleClick = this.handleClick.bind(this);
      this.handleDialogClose = this.handleDialogClose.bind(this);
      this.handleDialogCancel = this.handleDialogCancel.bind(this);
      this.queueMasonryLayout = this.queueMasonryLayout.bind(this);

      this.addEventListener('click', this.handleClick);
      this.dialog?.addEventListener('close', this.handleDialogClose);
      this.dialog?.addEventListener('cancel', this.handleDialogCancel);

      this.setupMasonry();
      this.setupPreloading();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.handleClick);
      this.dialog?.removeEventListener('close', this.handleDialogClose);
      this.dialog?.removeEventListener('cancel', this.handleDialogCancel);
      this.resizeObserver?.disconnect();
      this.sectionObserver?.disconnect();
      this.masonryFrame && window.cancelAnimationFrame(this.masonryFrame);
    }

    handleClick(event) {
      const closeButton = event.target.closest('[data-gallery-close]');
      if (closeButton) {
        this.dialog?.close();
        return;
      }

      const trigger = event.target.closest('[data-gallery-trigger]');
      if (trigger) {
        const card = trigger.closest('[data-gallery-card]');
        if (!card || card.dataset.hasVideo !== 'true') return;
        this.lastTrigger = trigger;
        this.openCard(card);
        return;
      }

      if (event.target === this.dialog) {
        this.dialog.close();
      }
    }

    handleDialogCancel() {
      document.documentElement.removeAttribute('scroll-lock');
    }

    handleDialogClose() {
      this.teardownPlayer();
      document.documentElement.removeAttribute('scroll-lock');

      if (this.lastTrigger && typeof this.lastTrigger.focus === 'function') {
        this.lastTrigger.focus();
      }
    }

    setupMasonry() {
      if (!this.grid || !window.ResizeObserver) return;

      this.classList.add('is-masonry-ready');
      this.resizeObserver = new ResizeObserver(() => this.queueMasonryLayout());
      this.resizeObserver.observe(this.grid);

      for (const card of this.cards) {
        const inner = card.querySelector('[data-gallery-item-inner]');
        if (inner) this.resizeObserver.observe(inner);

        const media = card.querySelector('img, svg');
        if (media && media.tagName === 'IMG' && !media.complete) {
          media.addEventListener('load', this.queueMasonryLayout, { once: true });
        }
      }

      this.queueMasonryLayout();
    }

    queueMasonryLayout() {
      if (!this.grid) return;

      if (this.masonryFrame) window.cancelAnimationFrame(this.masonryFrame);
      this.masonryFrame = window.requestAnimationFrame(() => this.applyMasonryLayout());
    }

    applyMasonryLayout() {
      const styles = window.getComputedStyle(this.grid);
      const rowHeight = Number.parseFloat(styles.getPropertyValue('grid-auto-rows'));
      const rowGap = Number.parseFloat(styles.getPropertyValue('row-gap'));

      if (!rowHeight) return;

      for (const card of this.cards) {
        const inner = card.querySelector('[data-gallery-item-inner]');
        if (!inner) continue;

        const height = inner.getBoundingClientRect().height;
        const span = Math.max(1, Math.ceil((height + rowGap) / (rowHeight + rowGap)));
        card.style.gridRowEnd = `span ${span}`;
      }
    }

    setupPreloading() {
      if (!this.cards.length) return;

      if (!('IntersectionObserver' in window)) {
        this.startBackgroundPreload();
        return;
      }

      this.sectionObserver = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (!entry.isIntersecting) continue;
            this.startBackgroundPreload();
            this.sectionObserver.disconnect();
            break;
          }
        },
        { rootMargin: '360px 0px' }
      );

      this.sectionObserver.observe(this);
    }

    startBackgroundPreload() {
      if (this.preloadStarted) return;
      this.preloadStarted = true;

      let videoIndex = 0;

      for (const card of this.cards) {
        if (card.dataset.hasVideo !== 'true') continue;
        this.preloadQueue.push({
          card,
          mode: videoIndex < 2 ? 'auto' : 'metadata',
        });
        videoIndex += 1;
      }

      this.runWhenIdle(() => this.processPreloadQueue());
    }

    processPreloadQueue() {
      if (this.activePreload) return;

      const next = this.preloadQueue.shift();
      if (!next) return;

      this.activePreload = true;

      this.preloadCard(next.card, next.mode)
        .catch(() => {})
        .finally(() => {
          this.activePreload = false;
          this.runWhenIdle(() => this.processPreloadQueue());
        });
    }

    preloadCard(card, mode) {
      const payload = this.getPayload(card);
      if (!payload || !Array.isArray(payload.sources) || payload.sources.length === 0) {
        return Promise.resolve();
      }

      const preloadVideo = document.createElement('video');
      preloadVideo.muted = true;
      preloadVideo.preload = mode;
      preloadVideo.playsInline = true;
      preloadVideo.tabIndex = -1;
      preloadVideo.setAttribute('aria-hidden', 'true');

      for (const source of payload.sources) {
        if (!source || !source.url) continue;

        const sourceElement = document.createElement('source');
        sourceElement.src = source.url;
        if (source.mime_type) sourceElement.type = source.mime_type;
        preloadVideo.append(sourceElement);
      }

      this.preloadBin?.append(preloadVideo);

      return new Promise((resolve) => {
        let settled = false;

        const finish = () => {
          if (settled) return;
          settled = true;
          card.dataset.preloaded = 'true';
          resolve();
        };

        preloadVideo.addEventListener(mode === 'auto' ? 'loadeddata' : 'loadedmetadata', finish, { once: true });
        preloadVideo.addEventListener('error', finish, { once: true });
        window.setTimeout(finish, mode === 'auto' ? 4000 : 2500);
        preloadVideo.load();
      });
    }

    openCard(card) {
      const payload = this.getPayload(card);
      if (!payload || !Array.isArray(payload.sources) || payload.sources.length === 0 || !this.dialog || !this.stage) return;

      if (this.dialogTitle) this.dialogTitle.textContent = payload.title || '';

      if (this.dialogCaption) {
        if (payload.caption) {
          this.dialogCaption.hidden = false;
          this.dialogCaption.textContent = payload.caption;
        } else {
          this.dialogCaption.hidden = true;
          this.dialogCaption.textContent = '';
        }
      }

      this.teardownPlayer();
      const { frame, video } = this.buildVideoPlayer(payload);
      this.stage.replaceChildren(frame);
      if (this.loading) this.loading.hidden = false;

      video.addEventListener(
        'loadeddata',
        () => {
          if (this.loading) this.loading.hidden = true;
        },
        { once: true }
      );
      video.addEventListener(
        'error',
        () => {
          if (this.loading) this.loading.hidden = true;
        },
        { once: true }
      );

      document.documentElement.setAttribute('scroll-lock', '');

      if (!this.dialog.open && typeof this.dialog.showModal === 'function') {
        this.dialog.showModal();
      } else if (!this.dialog.open) {
        this.dialog.setAttribute('open', 'open');
      }

      video.load();

      const playAttempt = video.play();
      if (playAttempt && typeof playAttempt.catch === 'function') {
        playAttempt.catch(() => {});
      }
    }

    buildVideoPlayer(payload) {
      const frame = document.createElement('div');
      frame.className = 'video-gallery__player-frame';

      const popupAspectRatio = payload.aspect_ratio || {};
      const popupAspectRatioValue = Number.parseFloat(popupAspectRatio.value);
      if (Number.isFinite(popupAspectRatioValue) && popupAspectRatioValue > 0) {
        frame.style.setProperty('--video-gallery-popup-ratio-value', String(popupAspectRatioValue));
      }

      const video = document.createElement('video');
      video.className = 'video-gallery__player-video';

      if (Number.isFinite(popupAspectRatioValue) && popupAspectRatioValue > 0) {
        video.style.setProperty('--video-gallery-popup-ratio-value', String(popupAspectRatioValue));
      }

      video.controls = true;
      video.autoplay = true;
      video.playsInline = true;
      video.preload = 'auto';
      video.setAttribute('controlsList', 'nodownload');
      if (payload.loop) video.loop = true;

      for (const source of payload.sources) {
        if (!source || !source.url) continue;
        const sourceElement = document.createElement('source');
        sourceElement.src = source.url;
        if (source.mime_type) sourceElement.type = source.mime_type;
        video.append(sourceElement);
      }

      frame.append(video);
      return { frame, video };
    }

    teardownPlayer() {
      if (!this.stage) return;

      const activeVideo = this.stage.querySelector('video');
      if (activeVideo) {
        activeVideo.pause();

        for (const source of activeVideo.querySelectorAll('source')) {
          source.removeAttribute('src');
        }

        activeVideo.removeAttribute('src');
        activeVideo.load();
      }

      this.stage.replaceChildren();
      if (this.loading) this.loading.hidden = true;
    }

    getPayload(card) {
      if (!card) return null;
      if (card.videoPayload) return card.videoPayload;

      const payloadElement = card.querySelector('[data-gallery-video]');
      if (!payloadElement) return null;

      try {
        card.videoPayload = JSON.parse(payloadElement.textContent);
      } catch (error) {
        card.videoPayload = null;
      }

      return card.videoPayload;
    }

    runWhenIdle(callback) {
      if ('requestIdleCallback' in window) {
        window.requestIdleCallback(callback, { timeout: 1200 });
        return;
      }

      window.setTimeout(callback, 240);
    }
  }

  customElements.define('video-gallery', PopupVideoGallery);
}
